import {
  objectsToCSV,
  downloadCSV,
  generateExportFilename,
} from "@/lib/utils/export/csv-export";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types";
import type { ExportFormat } from "../types";

/**
 * One exportable column: the field id plus the label/type the PDF and DOCX
 * writers need. CSV derives its own header row from the data keys.
 */
export interface ExportFieldDescriptor {
  label: string;
  type: string;
}

/** `[fieldId, descriptor]` pairs, in config order. */
export type ExportFieldEntry = [string, ExportFieldDescriptor];

/**
 * The columns an export writes: every configured field EXCEPT `formOnly` ones
 * (those exist only to render a create/edit form and have no cell value), with
 * `type` defaulting to `"text"`.
 *
 * Deliberately independent of the table's *visible* columns — an export is of
 * the entity, not of the current column layout.
 */
export function getExportFields<T extends BaseEntity>(
  config: SimpleTableConfig<T>,
): ExportFieldEntry[] {
  return Object.entries(config.fields)
    .filter(([, field]) => !field.formOnly)
    .map(
      ([id, field]) =>
        [id, { label: field.label, type: field.type || "text" }] as ExportFieldEntry,
    );
}

/**
 * Writes `exportData` in the requested format. PDF and DOCX writers are
 * imported lazily — they pull heavy vendor bundles that must not ship with the
 * list page.
 *
 * `setProgress` is optional so the current-page export (instant, no progress
 * bar) can share this dispatch with the all-pages export.
 */
export async function performFormatExport(
  exportData: Record<string, unknown>[],
  exportFormat: ExportFormat,
  fields: ExportFieldEntry[],
  entityName: string,
  setProgress?: (n: number) => void,
): Promise<void> {
  if (exportFormat === "csv") {
    const csvContent = objectsToCSV(exportData);
    const filename = generateExportFilename(entityName);
    setProgress?.(95);
    downloadCSV(csvContent, filename);
    return;
  }

  const columns = fields.map(([id, fieldConfig]) => ({
    id,
    label: fieldConfig.label,
    type: fieldConfig.type,
  }));
  const subtitle = `Generated on ${new Date().toLocaleDateString()}`;

  if (exportFormat === "pdf") {
    const { objectsToPDF, generatePDFFilename } = await import(
      "@/lib/utils/pdf/pdf-export-universal"
    );
    setProgress?.(90);
    objectsToPDF(exportData, columns, {
      filename: generatePDFFilename(entityName),
      title: entityName,
      subtitle,
    });
    setProgress?.(95);
    return;
  }

  if (exportFormat === "docx") {
    const { exportToDOCX, generateDOCXFilename } = await import("@/lib/utils/docx-export");
    setProgress?.(90);
    await exportToDOCX(exportData, columns, {
      title: entityName,
      subtitle,
      filename: generateDOCXFilename(entityName),
    });
    setProgress?.(95);
  }
}
